import { Card, Text, Title } from "@mantine/core";
import { useState, useEffect, useRef } from "react";
import { useReferral } from "../../scripts/ReferralContext";

interface AppCardProps {
  url: string;
  front: {
    logo: string;
    color: string;
  };
  back: {
    name: string;
    text: string;
  }
}

interface FlipCardProps {
  card?: AppCardProps;
  hover?: boolean;
}

export function AppCard({ url, front, back }: AppCardProps) {
  const { getUrlWithRef } = useReferral(); // Add this hook

  return (
    <FlipCard
      card={{ url: getUrlWithRef(url), front, back }} // Wrap url here
      hover={true}
    />
  );
}

function FlipCard({
    card,
    hover = false,
}: FlipCardProps) {
    const [flipped, setFlipped] = useState(false);
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    // Detect touch device on mount
    useEffect(() => {
        const checkTouch = () => {
            setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
        };
        checkTouch();
    }, []);

    // Handle clicks outside the card on touch devices
    useEffect(() => {
        if (!isTouchDevice || !flipped) return;

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
                setFlipped(false);
            }
        };

        // Add a small delay to prevent immediate flip back
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isTouchDevice, flipped]);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!flipped) {
            // First touch: flip the card
            e.preventDefault();
            setFlipped(true);
        }
        // If already flipped, let the touch propagate to trigger navigation
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // On touch devices, only navigate if already flipped
        if (isTouchDevice && flipped && card?.url) {
            window.open(card.url, '_self');
        } else if (isTouchDevice && !flipped) {
            // First click on touch device: flip
            e.preventDefault();
            setFlipped(true);
        } else if (!isTouchDevice && flipped && card?.url) {
            // Desktop: navigate when back is clicked
            e.stopPropagation();
            window.open(card.url, '_self');
        }
    };

    return (
        <div 
            ref={cardRef}
            className={`flip-container ${hover && !isTouchDevice ? 'hover-enabled' : ''}`}
            onMouseEnter={hover && !isTouchDevice ? () => setFlipped(true) : undefined}
            onMouseLeave={hover && !isTouchDevice ? () => setFlipped(false) : undefined}
            onTouchStart={handleTouchStart}
        >
            <div className={`flipper ${flipped ? 'flipped' : ''}`} onClick={handleCardClick}>
                {/* Front face */}
                <div className="card-face front">
                    <Card 
                        style={{ 
                            backgroundColor: card?.front.color,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            overflow: 'hidden'
                        }}
                        shadow="lg"
                        radius="lg"
                    >
                        <div style={{ 
                            width: '52.5%', 
                            height: '52.5%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            {card?.front.logo && (
                                <img
                                    src={card.front.logo}
                                    alt={card?.back.name || 'Logo'}
                                    style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            )}
                        </div>
                    </Card>
                </div>

                {/* Back face */}
                <div className="card-face back">
                    <Card 
                        shadow="lg" 
                        radius="lg"
                        style={{ 
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#1a1a1a',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            border: 'none',
                            transition: 'transform 0.2s ease'
                        }}
                        className="back-card"
                    >
                        <div style={{ 
                            padding: '1.5rem',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <div style={{ flex: 1 }}>
                                <Title 
                                    order={2} 
                                    style={{ 
                                        fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
                                        marginBottom: '1rem',
                                        fontWeight: 700,
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    {card?.back.name}
                                </Title>
                                {card?.back.text && (
                                    <Text 
                                        style={{ 
                                            fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                                            lineHeight: 1.6,
                                            opacity: 0.9
                                        }}
                                    >
                                        {card?.back.text}
                                    </Text>
                                )}
                            </div>
                            <div style={{ 
                                marginTop: 'auto',
                                paddingTop: '1rem',
                                borderTop: '1px solid rgba(255,255,255,0.1)'
                            }}>
                                <Text 
                                    size="sm" 
                                    style={{ 
                                        opacity: 0.7,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    Click to visit →
                                </Text>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            <style>{`
                .flip-container {
                    perspective: 1500px;
                    width: 100%;
                    aspect-ratio: 3 / 2;
                    min-height: 300px;
                    max-width: 580px;
                    margin: 0 auto;
                    touch-action: manipulation;
                }

                @media (max-width: 640px) {
                    .flip-container {
                        aspect-ratio: 4 / 3;
                        min-height: 260px;
                    }
                }

                .flip-container.hover-enabled:hover .flipper {
                    transform: rotateY(180deg);
                }

                .flip-container:not(.hover-enabled) {
                    cursor: pointer;
                }

                .flipper {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    transition: transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1);
                    transform-style: preserve-3d;
                }

                .flipper.flipped {
                    transform: rotateY(180deg);
                }

                .card-face {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                    border-radius: 12px;
                    overflow: hidden;
                }

                .card-face.front {
                    transform: rotateY(0deg);
                    z-index: 2;
                }

                .card-face.back {
                    transform: rotateY(180deg);
                }

                .back-card:hover {
                    transform: scale(1.02);
                }

                .back-card:active {
                    transform: scale(0.98);
                }

                /* Prevent text selection during flip */
                .flip-container * {
                    user-select: none;
                    -webkit-user-select: none;
                    -webkit-touch-callout: none;
                }

                /* Smooth animations on mobile */
                @media (max-width: 768px) {
                    .flipper {
                        transition-duration: 0.5s;
                    }
                }

                /* Improve rendering performance */
                .flipper,
                .card-face {
                    transform-style: preserve-3d;
                    will-change: transform;
                }
            `}</style>
        </div>
    );
}